import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLanguagePrefix } from "@/hooks/use-language-prefix";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowRight, Play, ShieldCheck, Landmark, SlidersHorizontal, CloudCog, ServerCog } from "lucide-react";
import { ScrambleCycle } from "@/components/ui/scramble-cycle";
import { OrbitSystem, GlowOrb, DashboardCard, MiniBarChart, MiniSparkline } from "./AnimatedObjects";
import FlowLines from "./FlowLines";

const HologramCube3D = lazy(() => import("@/components/HologramCube3D"));

// Defer non-critical interactive bits (counter intervals, scramble animations)
// until the browser is idle to reduce TBT/TTI on initial paint.
const useIdleReady = (delay = 1500) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const trigger = () => { if (!cancelled) setReady(true); };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(trigger, { timeout: delay });
      return () => { cancelled = true; window.cancelIdleCallback?.(id); };
    } else {
      const id = window.setTimeout(trigger, delay);
      return () => { cancelled = true; clearTimeout(id); };
    }
    return () => { cancelled = true; };
  }, [delay]);
  return ready;
};

/**
 * Defers loading the heavy Three.js hero cube (≈230KB of vendor JS, 8.5s of
 * scripting on cold loads) until the browser has been idle for longer AND
 * the user has interacted with the page (or scrolled). Drastically reduces
 * Max Potential FID and TBT without affecting layout (a same-sized
 * placeholder reserves the slot).
 */
const useCubeReady = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    const trigger = () => { if (!cancelled) setReady(true); };

    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const armIdle = () => {
      if (typeof w.requestIdleCallback === "function") {
        idleId = w.requestIdleCallback(trigger, { timeout: 8000 });
      } else {
        timeoutId = window.setTimeout(trigger, 1500);
      }
    };

    // Mount on first user interaction (pointer/scroll/key) — strongest
    // signal that the main thread is no longer competing for FID-critical work.
    const onInteract = () => trigger();
    const opts: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener("pointerdown", onInteract, opts);
    window.addEventListener("scroll", onInteract, opts);
    window.addEventListener("keydown", onInteract, opts);

    // Fallback: arm idle path with a longer delay so the cube still appears
    // on idle desktop visits without user input.
    armIdle();
    // Idle callbacks may starve on busy desktops; keep a bounded wait.
    const maxWaitId = window.setTimeout(trigger, 1500);

    return () => {
      cancelled = true;
      window.clearTimeout(maxWaitId);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("keydown", onInteract);
      if (idleId !== null && typeof (w as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback === "function") {
        (w as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [ready]);
  return ready;
};

const AnimatedCounter = ({ target, suffix = "", label }: { target: number; suffix?: string; label: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60;
        const inc = target / steps;
        let cur = 0;
        const t = setInterval(() => {
          cur += inc;
          if (cur >= target) { setCount(target); clearInterval(t); }
          else setCount(Math.floor(cur));
        }, 2000 / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-primary-foreground font-heading tracking-tight">{count}{suffix}</div>
      <div className="text-xs md:text-sm text-primary-foreground/70 mt-1 tracking-wide uppercase">{label}</div>
    </div>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  const prefix = useLanguagePrefix();
  const isMobile = useIsMobile();
  const idleReady = useIdleReady(1500);
  const cubeReady = useCubeReady();

  const trustItems = [
    { icon: ShieldCheck, text: t("trust.regulated") },
    { icon: Landmark, text: t("trust.banking") },
    { icon: SlidersHorizontal, text: t("trust.flexible") },
    { icon: CloudCog, text: t("trust.saas") },
    { icon: ServerCog, text: t("trust.scalable") },
  ];

  return (
    <>
      <section className="relative min-h-screen gradient-hero overflow-hidden flex items-center">
        <div className="absolute inset-0 gradient-hero-radial" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-glow/20 to-transparent animate-scan" />
        </div>

        <FlowLines variant="hero" />

        <div className="container mx-auto px-4 lg:px-8 pt-28 pb-16 lg:pt-28 lg:pb-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>
                <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-blue-glow/20 bg-blue-glow/[0.06] text-blue-glow text-xs font-semibold mb-8 backdrop-blur-sm tracking-widest uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-glow animate-pulse" />
                  {t("hero.badge")}
                </span>
              </motion.div>

              {/* H1 semántico: texto completo y estable en el DOM desde el primer render.
                  No depende de JavaScript ni de animaciones para tener sentido. */}
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] xl:text-[3.75rem] font-bold text-primary-foreground leading-[1.08] mb-8 tracking-tight">
                {t("hero.title1")}{" "}
                <span className="text-gradient">{t("hero.typewriter3")}</span>{" "}
                <br className="hidden md:block" />
                {t("hero.title2")}
              </h1>

              {/* Capa decorativa: conserva la identidad animada sin alterar el H1. */}
              <div aria-hidden="true" className="hidden md:flex items-center gap-2 -mt-4 mb-6 text-sm text-blue-glow/70 font-medium tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-glow/60" />
                {idleReady ? (
                  <ScrambleCycle
                    texts={[
                      t("hero.titleHighlight"),
                      t("hero.typewriter2"),
                      t("hero.typewriter3"),
                    ]}
                    interval={3500}
                    duration={0.8}
                    speed={0.04}
                  />
                ) : (
                  t("hero.titleHighlight")
                )}
              </div>


              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}
                className="text-base md:text-lg text-primary-foreground/45 mb-6 max-w-xl leading-relaxed font-light">
                {t("hero.subtitle")}
              </motion.p>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.2 }}
                className="text-xs text-primary-foreground/30 mb-8 uppercase tracking-widest font-medium">
                {t("hero.microcopy")}
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.25 }}
                className="mb-16">
                <Link to={`${prefix}/contacto`}>
                  <Button variant="cta" size="lg" className="text-base px-8 py-6 shadow-cta group w-full sm:w-auto rounded-xl">
                    {t("hero.cta1")} <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <div className="flex flex-wrap gap-4 mt-3">
                  {(Array.isArray(t("hero.trustSignals", { returnObjects: true })) ? (t("hero.trustSignals", { returnObjects: true }) as string[]) : []).map((signal) => (
                    <span key={signal} className="text-primary-foreground/30 text-xs flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-blue-glow/40" />
                      {signal}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.3 }}
                className="flex gap-8 md:gap-14 border-t border-primary-foreground/[0.08] pt-8">
                <AnimatedCounter target={60} suffix="+" label={t("hero.statClients")} />
                <AnimatedCounter target={30} suffix="M+" label={t("hero.statTransactions")} />
                <AnimatedCounter target={8} label={t("hero.statCountries")} />
              </motion.div>
            </div>

            {!isMobile && (
            <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="hidden lg:flex items-center justify-center relative min-h-[560px] overflow-visible">
              <OrbitSystem size={460} className="-translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2" duration={30} />
              <OrbitSystem size={380} ringColor="border-purple-accent/10" dotColor="bg-purple-accent" className="-translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2" duration={22} reverse />
              <OrbitSystem size={530} ringColor="border-orange-cta/8" dotColor="bg-orange-cta" className="-translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2" duration={35} />

              <div className="relative z-10">
                {cubeReady ? (
                  <Suspense fallback={<div className="w-[480px] h-[480px]" />}>
                    <HologramCube3D />
                  </Suspense>
                ) : (
                  <div className="w-[480px] h-[480px]" aria-hidden />
                )}
              </div>

              <DashboardCard className="top-8 right-2" delay={1.2}>
                <div className="px-5 py-4 min-w-[160px]">
                  <div className="text-[10px] text-primary-foreground/40 uppercase tracking-widest mb-1">{t("hero.dashboardPrecision")}</div>
                  <div className="text-2xl font-bold text-blue-glow font-heading">{t("hero.dashboardPrecisionValue")}</div>
                  <div className="text-[10px] text-primary-foreground/30 mb-2">{t("hero.dashboardPrecisionLabel")}</div>
                  <MiniSparkline />
                </div>
              </DashboardCard>

              <DashboardCard className="bottom-16 left-2" delay={1.6}>
                <div className="px-5 py-4 min-w-[160px]">
                  <div className="text-[10px] text-primary-foreground/40 uppercase tracking-widest mb-1">{t("hero.dashboardFP")}</div>
                  <div className="text-2xl font-bold text-gradient-warm font-heading">{t("hero.dashboardFPValue")}</div>
                  <div className="text-[10px] text-primary-foreground/30 mb-2">{t("hero.dashboardFPLabel")}</div>
                  <MiniBarChart />
                </div>
              </DashboardCard>

              <DashboardCard className="top-[45%] right-3" delay={2}>
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-glow animate-pulse" />
                    <span className="text-[10px] text-primary-foreground/50">{t("hero.dashboardMonitoring")}</span>
                  </div>
                  <div className="text-sm font-semibold text-primary-foreground/80 mt-1">{t("hero.dashboardOps")}</div>
                </div>
              </DashboardCard>
            </motion.div>
            )}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
      </section>

      <section className="py-5 bg-card/80 backdrop-blur-sm border-b border-border/30 relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {trustItems.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.1 }}
                className="flex items-center gap-2.5 text-secondary-foreground text-xs font-bold tracking-wide uppercase">
                <item.icon className="w-4 h-4 text-blue-corporate" />
                <span>{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
